import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { ArrowLeft, Clock, Users, Minus, Plus, Trash2, Zap, UtensilsCrossed, ShoppingCart, Check, Heart, Printer, PackageCheck, PackageX, Image as ImageIcon, Sparkles, Upload, ExternalLink, ChefHat, CalendarPlus } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import AddToPlanDialog from "@/components/AddToPlanDialog";

export default function RecipeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, activeBoat } = useAuth();
  const activeShift = (user?.preferred_shift === "1" || user?.preferred_shift === "2")
    ? user.preferred_shift
    : (() => { try { return localStorage.getItem("pb-active-shift") || "1"; } catch { return "1"; } })();
  const isImageAdmin =
    user?.role === "admin" ||
    activeBoat?.members?.some((m) => m.user_id === user?.user_id && ["owner", "admin", "cook"].includes(m.role));
  const [recipe, setRecipe] = useState(null);
  const [servings, setServings] = useState(6);
  const [nutritionLoading, setNutritionLoading] = useState(false);
  const [addedItems, setAddedItems] = useState({});
  const [addingAll, setAddingAll] = useState(false);
  const [pantryItems, setPantryItems] = useState([]);
  const [favLoading, setFavLoading] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [consumeLoading, setConsumeLoading] = useState(false);
  const [consumeOpen, setConsumeOpen] = useState(false);
  const [consumeResult, setConsumeResult] = useState(null);
  const [addToPlanOpen, setAddToPlanOpen] = useState(false);

  const loadRecipe = useCallback(async () => {
    try {
      const res = await api.getRecipe(id);
      setRecipe(res.data);
      setServings(res.data.servings || 6);
    } catch { toast.error("Kunne ikke laste oppskrift"); navigate("/recipes"); }
  }, [id, navigate]);

  const loadPantry = useCallback(async () => {
    try {
      const res = await api.getPantry();
      setPantryItems(res.data);
    } catch (err) { console.warn("loadPantry failed", err); /* silent for UX */ }
  }, []);

  useEffect(() => { loadRecipe(); loadPantry(); }, [loadRecipe, loadPantry]);

  const handleDelete = async () => {
    try {
      await api.deleteRecipe(id);
      toast.success("Oppskrift slettet");
      navigate("/recipes");
    } catch { toast.error("Feil ved sletting"); }
  };

  const toggleFavorite = async () => {
    setFavLoading(true);
    try {
      const res = await api.toggleFavorite(id);
      setRecipe((p) => ({ ...p, is_favorite: res.data.is_favorite }));
      toast.success(res.data.is_favorite ? "Lagt til i favoritter" : "Fjernet fra favoritter");
    } catch { toast.error("Feil ved favorittmerking"); }
    finally { setFavLoading(false); }
  };

  const calcNutrition = async () => {
    setNutritionLoading(true);
    try {
      const res = await api.calculateNutrition(id);
      setRecipe((p) => ({ ...p, nutrition: res.data }));
      toast.success("Naeringsinnhold beregnet!");
    } catch { toast.error("Feil ved beregning"); }
    finally { setNutritionLoading(false); }
  };

  const scaleAmount = (amount) => {
    const num = parseFloat(amount);
    if (isNaN(num)) return amount;
    const scaled = (num / (recipe?.servings || 6)) * servings;
    return scaled % 1 === 0 ? scaled.toString() : scaled.toFixed(1);
  };

  // Check if ingredient is available in pantry
  const getPantryMatch = (ingName) => {
    const name = ingName.toLowerCase().trim();
    return pantryItems.find((p) => {
      const pName = p.name.toLowerCase().trim();
      return pName.includes(name) || name.includes(pName);
    });
  };

  const addToShoppingList = async (ing, index) => {
    try {
      const weekStart = (await import("@/lib/api")).getWeekStart();
      const slRes = await api.getShoppingList(weekStart, "", activeShift);
      const sl = slRes.data;
      const newItem = {
        id: crypto.randomUUID(),
        name: ing.name,
        amount: scaleAmount(ing.amount),
        unit: ing.unit,
        category: ing.category || "Other",
        checked: false,
        status: "",
        is_manual: true,
      };
      const updated = { ...sl, items: [...(sl.items || []), newItem] };
      await api.updateShoppingList(weekStart, updated, "", activeShift);
      setAddedItems((p) => ({ ...p, [index]: true }));
      toast.success(`${ing.name} lagt til i handlelisten`);
    } catch { toast.error("Feil ved tillegging"); }
  };

  const addAllToShoppingList = async () => {
    if (!recipe?.ingredients?.length) return;
    setAddingAll(true);
    try {
      const weekStart = (await import("@/lib/api")).getWeekStart();
      const slRes = await api.getShoppingList(weekStart, "", activeShift);
      const sl = slRes.data;
      const newItems = recipe.ingredients.map((ing) => ({
        id: crypto.randomUUID(),
        name: ing.name,
        amount: scaleAmount(ing.amount),
        unit: ing.unit,
        category: ing.category || "Other",
        checked: false,
        status: "",
        is_manual: true,
      }));
      const updated = { ...sl, items: [...(sl.items || []), ...newItems] };
      await api.updateShoppingList(weekStart, updated, "", activeShift);
      const all = {};
      recipe.ingredients.forEach((_, i) => { all[i] = true; });
      setAddedItems(all);
      toast.success(`${recipe.ingredients.length} ingredienser lagt til i handlelisten`);
    } catch { toast.error("Feil"); }
    finally { setAddingAll(false); }
  };

  const handlePrint = () => window.print();

  const handleConsume = async () => {
    if (consumeLoading) return;
    const scale = servings / (recipe?.servings || servings || 1);
    setConsumeLoading(true);
    try {
      const res = await api.consumeRecipe(id, scale);
      setConsumeResult(res.data);
      const usedCount = res.data.used_count || 0;
      const missingCount = res.data.missing_count || 0;
      if (usedCount > 0 && missingCount === 0) {
        toast.success(`Brukt ${usedCount} varer fra beholdningen`);
      } else if (usedCount > 0 && missingCount > 0) {
        toast.success(`Brukt ${usedCount} varer · ${missingCount} manglet`);
      } else {
        toast.warning("Ingen varer matchet beholdningen");
      }
      // Refresh pantry so updated amounts show immediately
      await loadPantry();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Feil ved fratrekk fra beholdning");
    } finally {
      setConsumeLoading(false);
    }
  };

  if (!recipe) return (
    <div className="p-6 max-w-3xl mx-auto animate-pulse space-y-4">
      <div className="h-8 bg-stone-200 rounded w-64" />
      <div className="h-48 bg-stone-100 rounded-xl" />
    </div>
  );

  const images = [
    "https://images.unsplash.com/photo-1473093295043-cdd812d0e601?auto=format&fit=crop&q=80&w=800",
    "https://images.unsplash.com/photo-1490645935967-10de6ba17061?auto=format&fit=crop&q=80&w=800",
    "https://images.unsplash.com/photo-1512621776951-a57141f2eefd?auto=format&fit=crop&q=80&w=800",
  ];
  const imgIdx = recipe.title.length % images.length;
  const heroImg = recipe.image_url && recipe.image_url.trim()
    ? recipe.image_url
    : recipe.has_image
      ? `${process.env.REACT_APP_BACKEND_URL}/api/recipes/${recipe.id}/image?v=${Date.now()}`
      : images[imgIdx];

  const handleRegenerateImage = async () => {
    if (imageLoading) return;
    setImageLoading(true);
    toast.loading("AI genererer nytt bilde...", { id: "regen-img" });
    try {
      const res = await api.regenerateRecipeImage(recipe.id);
      setRecipe((p) => ({ ...p, image_url: res.data.image_url }));
      toast.success("Nytt bilde generert", { id: "regen-img" });
      setImageDialogOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke generere bilde", { id: "regen-img" });
    } finally {
      setImageLoading(false);
    }
  };

  const handleUploadImage = async (file) => {
    if (!file || imageLoading) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Bildet er for stort (maks 5 MB)"); return; }
    setImageLoading(true);
    toast.loading("Laster opp bildet...", { id: "upload-img" });
    try {
      const res = await api.uploadRecipeImage(recipe.id, file);
      setRecipe((p) => ({ ...p, image_url: res.data.image_url }));
      toast.success("Bilde oppdatert", { id: "upload-img" });
      setImageDialogOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke laste opp bildet", { id: "upload-img" });
    } finally {
      setImageLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto" data-testid="recipe-detail-page">
      {/* Hero */}
      <div className="relative h-56 md:h-72 overflow-hidden print-hide">
        <img src={heroImg} alt={recipe.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-stone-900/70 via-stone-900/20 to-transparent" />
        <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
          <button onClick={() => navigate("/recipes")} className="flex items-center gap-2 rounded-lg bg-white/90 backdrop-blur-sm px-3 py-2 text-sm font-medium text-stone-700 hover:bg-white transition-colors" data-testid="back-to-recipes">
            <ArrowLeft className="h-4 w-4" /> Tilbake
          </button>
          <div className="flex gap-2">
            <button onClick={() => setAddToPlanOpen(true)} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-navy-800 text-white hover:bg-navy-900 transition-colors text-xs font-medium" title="Legg til i ukeplan" data-testid="open-add-to-plan-btn">
              <CalendarPlus className="h-4 w-4" />
              <span className="hidden sm:inline">Legg til i plan</span>
            </button>
            <button onClick={() => setImageDialogOpen(true)} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/90 backdrop-blur-sm text-stone-700 hover:bg-white transition-colors text-xs font-medium" title="Bytt bilde" data-testid="change-image-btn">
              <ImageIcon className="h-4 w-4" />
              <span className="hidden sm:inline">Bytt bilde</span>
            </button>
            <button onClick={toggleFavorite} disabled={favLoading} className={`p-2 rounded-lg backdrop-blur-sm transition-colors ${recipe.is_favorite ? "bg-red-500/90 text-white hover:bg-red-600" : "bg-white/90 text-stone-600 hover:bg-white"}`} data-testid="toggle-favorite-btn">
              <Heart className={`h-4 w-4 ${recipe.is_favorite ? "fill-current" : ""}`} />
            </button>
            <button onClick={handlePrint} className="p-2 rounded-lg bg-white/90 backdrop-blur-sm text-stone-600 hover:bg-white transition-colors" data-testid="print-recipe-btn">
              <Printer className="h-4 w-4" />
            </button>
            <button onClick={handleDelete} className="p-2 rounded-lg bg-red-500/90 backdrop-blur-sm text-white hover:bg-red-600 transition-colors" data-testid="delete-recipe-detail"><Trash2 className="h-4 w-4" /></button>
          </div>
        </div>
        <div className="absolute bottom-4 left-4 right-4">
          <span className="inline-block rounded-full bg-white/90 backdrop-blur-sm px-3 py-1 text-xs font-semibold text-stone-700 mb-2">{recipe.category || recipe.meal_type}</span>
          <h1 className="text-3xl md:text-4xl font-bold text-white" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{recipe.title}</h1>
        </div>
      </div>

      {/* Print header (visible only when printing) */}
      <div className="hidden print-show">
        <h1 className="text-3xl font-bold text-stone-900 mb-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{recipe.title}</h1>
        <p className="text-sm text-stone-500 mb-4">{recipe.category || recipe.meal_type} | {servings} porsjoner</p>
      </div>

      <div className="p-6 space-y-8">
        <div className="flex items-center gap-2 flex-wrap print-hide">
          <span className="text-xs uppercase tracking-wider text-stone-400 font-semibold">Kategori:</span>
          <select
            value={recipe.category || "Middag"}
            onChange={async (e) => {
              const newCat = e.target.value;
              const newMealType = ({"Frokost":"breakfast","Lunsj":"lunch","Middag":"dinner","Dessert":"dessert","Bakst":"baking"})[newCat] || recipe.meal_type;
              setRecipe((r) => ({ ...r, category: newCat, meal_type: newMealType, is_baking: newCat === "Bakst" }));
              try {
                await api.updateRecipe(recipe.id, { category: newCat, meal_type: newMealType, is_baking: newCat === "Bakst" });
                toast.success("Kategori oppdatert");
              } catch (err) { toast.error(err.response?.data?.detail || "Feil"); }
            }}
            className="text-xs rounded-md border border-stone-200 bg-white px-2 py-1 font-medium text-navy-800 focus:border-navy-800 outline-none"
            data-testid="edit-recipe-category"
          >
            {["Frokost", "Lunsj", "Middag", "Dessert", "Bakst", "Småretter", "Tilbehør", "Annet"].map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
        {recipe.description && <p className="text-base text-stone-600 leading-relaxed">{recipe.description}</p>}
        {recipe.source_url && (
          <a
            href={recipe.source_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs text-navy-800 hover:text-navy-900 underline font-medium"
            data-testid="recipe-source-link"
          >
            <ExternalLink className="h-3 w-3" /> Åpne original oppskrift
          </a>
        )}
        <div className="flex flex-wrap gap-4">
          {recipe.prep_time && <div className="flex items-center gap-2 text-sm text-stone-500"><Clock className="h-4 w-4" /> Prep: {recipe.prep_time}</div>}
          {recipe.cook_time && <div className="flex items-center gap-2 text-sm text-stone-500"><Clock className="h-4 w-4" /> Tilberedning: {recipe.cook_time}</div>}
        </div>

        {/* Servings adjuster */}
        <div className="flex items-center gap-3 p-4 bg-stone-50 rounded-xl border border-stone-200 print-hide" data-testid="servings-adjuster">
          <Users className="h-5 w-5 text-stone-400" />
          <span className="text-sm font-medium text-stone-600">Antall personer:</span>
          <button onClick={() => setServings(Math.max(1, servings - 1))} className="w-8 h-8 rounded-lg bg-white border border-stone-200 flex items-center justify-center text-stone-600 hover:bg-stone-100 transition-colors" data-testid="servings-minus"><Minus className="h-4 w-4" /></button>
          <span className="text-lg font-bold text-stone-800 w-8 text-center" data-testid="servings-value">{servings}</span>
          <button onClick={() => setServings(servings + 1)} className="w-8 h-8 rounded-lg bg-white border border-stone-200 flex items-center justify-center text-stone-600 hover:bg-stone-100 transition-colors" data-testid="servings-plus"><Plus className="h-4 w-4" /></button>
          <span className="text-xs text-stone-400 ml-1">(Oppskrift: {recipe.servings})</span>
        </div>

        {/* Ingredients with pantry status + add-to-shopping-list */}
        <div>
          <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
            <h2 className="text-xl font-semibold text-stone-800" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Ingredienser</h2>
            <div className="flex gap-2 flex-wrap print-hide">
              <button
                onClick={() => setConsumeOpen(true)}
                disabled={consumeLoading}
                className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 transition-colors disabled:opacity-50"
                data-testid="consume-recipe-btn"
                title="Trekk ingrediensene fra beholdningen"
              >
                <ChefHat className="h-4 w-4" />
                {consumeLoading ? "Trekker fra…" : "Laget — bruk fra beholdning"}
              </button>
              <button onClick={addAllToShoppingList} disabled={addingAll} className="inline-flex items-center gap-2 rounded-lg bg-navy-800 px-4 py-2 text-sm font-medium text-white hover:bg-navy-900 transition-colors disabled:opacity-50" data-testid="add-all-to-list-btn">
                <ShoppingCart className="h-4 w-4" />
                {addingAll ? "Legger til..." : "Legg alle i handleliste"}
              </button>
            </div>
          </div>
          <div className="bg-white border border-stone-100 rounded-xl divide-y divide-stone-50">
            {(recipe.ingredients || []).map((ing, i) => {
              const pantryMatch = getPantryMatch(ing.name);
              return (
                <div key={`ing-${i}-${ing.name || ""}`} className="flex items-center gap-3 px-4 py-3 group" data-testid={`ingredient-row-${i}`}>
                  {pantryMatch ? (
                    <span className="flex items-center gap-1 text-seawater-600" title={`Om bord: ${pantryMatch.amount} ${pantryMatch.unit || ""}`}>
                      <PackageCheck className="h-4 w-4 flex-shrink-0" />
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-amber-500" title="Ikke i beholdningen">
                      <PackageX className="h-4 w-4 flex-shrink-0" />
                    </span>
                  )}
                  <span className="text-sm font-semibold text-stone-700 min-w-[60px]">{scaleAmount(ing.amount)} {ing.unit}</span>
                  <span className="text-sm text-stone-600 flex-1">{ing.name}</span>
                  {pantryMatch && (
                    <span className="text-[10px] bg-sailcloth-100 text-navy-800 px-2 py-0.5 rounded-full font-medium print-hide" data-testid={`pantry-tag-${i}`}>
                      Om bord: {pantryMatch.amount} {pantryMatch.unit || ""}
                    </span>
                  )}
                  {addedItems[i] ? (
                    <span className="flex items-center gap-1 text-xs text-seawater-600 font-medium print-hide"><Check className="h-3.5 w-3.5" /> Lagt til</span>
                  ) : (
                    <button onClick={() => addToShoppingList(ing, i)} className="flex items-center gap-1 rounded-lg border border-stone-200 px-2.5 py-1 text-xs font-medium text-stone-500 hover:bg-sailcloth-100 hover:text-navy-800 hover:border-seawater-500 transition-all opacity-0 group-hover:opacity-100 print-hide" data-testid={`add-ingredient-${i}`}>
                      <ShoppingCart className="h-3 w-3" /> Handleliste
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Side dishes */}
        {(recipe.side_dishes || []).length > 0 && (
          <div>
            <h2 className="text-xl font-semibold text-stone-800 mb-4 flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              <UtensilsCrossed className="h-5 w-5" /> Tilbehor
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {recipe.side_dishes.map((sd, i) => (
                <div key={`sd-${i}-${sd.name || ""}`} className="p-3 bg-amber-50/50 rounded-xl border border-amber-100">
                  <span className="text-sm font-semibold text-stone-700">{sd.name}</span>
                  {sd.description && <p className="text-xs text-stone-500 mt-0.5">{sd.description}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Instructions */}
        <div>
          <h2 className="text-xl font-semibold text-stone-800 mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Fremgangsmate</h2>
          <div className="space-y-4">
            {(recipe.instructions || []).map((step, i) => (
              <div key={`step-${i}`} className="flex gap-4">
                <span className="w-8 h-8 rounded-full bg-navy-50 text-navy-800 flex items-center justify-center text-sm font-bold flex-shrink-0">{i + 1}</span>
                <p className="text-sm text-stone-600 leading-relaxed pt-1.5">{step}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Nutrition */}
        <div className="p-5 bg-stone-50 rounded-xl border border-stone-200" data-testid="nutrition-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xl font-semibold text-stone-800 flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}><Zap className="h-5 w-5" /> Naeringsinnhold</h2>
            <button onClick={calcNutrition} disabled={nutritionLoading} className="text-sm text-navy-800 hover:text-navy-800 font-medium disabled:opacity-50 print-hide" data-testid="calc-nutrition-btn">
              {nutritionLoading ? "Beregner..." : recipe.nutrition ? "Beregn pa nytt" : "Beregn med AI"}
            </button>
          </div>
          {recipe.nutrition ? (
            <div className="grid grid-cols-5 gap-3">
              {[
                { label: "Kalorier", value: recipe.nutrition.calories, unit: "kcal", color: "bg-orange-50 text-orange-700 border-orange-100" },
                { label: "Protein", value: recipe.nutrition.protein, unit: "g", color: "bg-blue-50 text-blue-700 border-blue-100" },
                { label: "Fett", value: recipe.nutrition.fat, unit: "g", color: "bg-amber-50 text-amber-700 border-amber-100" },
                { label: "Karbo", value: recipe.nutrition.carbs, unit: "g", color: "bg-green-50 text-green-700 border-green-100" },
                { label: "Fiber", value: recipe.nutrition.fiber, unit: "g", color: "bg-purple-50 text-purple-700 border-purple-100" },
              ].map((n) => (
                <div key={n.label} className={`rounded-xl p-3 text-center border ${n.color}`}>
                  <span className="text-2xl font-bold block">{n.value}</span>
                  <span className="text-[10px] uppercase tracking-wider font-semibold">{n.unit}</span>
                  <span className="text-[10px] block opacity-70">{n.label}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-stone-400 italic">Klikk "Beregn med AI" for a estimere naeringsinnhold</p>
          )}
        </div>
      </div>

      {/* Consume recipe from pantry dialog */}
      <Dialog open={consumeOpen} onOpenChange={(v) => { setConsumeOpen(v); if (!v) setConsumeResult(null); }}>
        <DialogContent className="max-w-md" data-testid="consume-recipe-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              {consumeResult ? "Brukt fra beholdningen" : "Bruk fra beholdningen"}
            </DialogTitle>
            <DialogDescription>
              {consumeResult
                ? "Oversikt over hva som ble trukket fra beholdningen."
                : `Trekk ingrediensene for ${recipe.title} fra beholdningen.`}
            </DialogDescription>
          </DialogHeader>
          {!consumeResult ? (
            <div className="space-y-4">
              <p className="text-sm text-stone-600">
                Vi trekker mengdene for <strong>{recipe.title}</strong> fra beholdningen, skalert til {servings} porsjoner
                {recipe.servings && recipe.servings !== servings ? ` (oppskriften er ${recipe.servings})` : ""}.
              </p>
              <p className="text-xs text-stone-500">
                Ingredienser som ikke finnes i beholdningen blir listet opp, slik at du enkelt kan legge dem til handlelista.
              </p>
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => setConsumeOpen(false)}
                  className="rounded-lg border border-stone-200 px-4 py-2 text-sm font-medium text-stone-600 hover:bg-stone-50"
                  data-testid="consume-cancel-btn"
                >
                  Avbryt
                </button>
                <button
                  onClick={handleConsume}
                  disabled={consumeLoading}
                  className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                  data-testid="consume-confirm-btn"
                >
                  {consumeLoading ? "Trekker fra…" : "Ja, trekk fra beholdningen"}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-lg bg-emerald-50 border border-emerald-100 p-2">
                  <div className="text-2xl font-bold text-emerald-700" data-testid="consume-used-count">{consumeResult.used_count || 0}</div>
                  <div className="text-[10px] uppercase tracking-wider text-emerald-700/80 font-semibold">Brukt</div>
                </div>
                <div className="rounded-lg bg-amber-50 border border-amber-100 p-2">
                  <div className="text-2xl font-bold text-amber-700" data-testid="consume-partial-count">{consumeResult.partial_count || 0}</div>
                  <div className="text-[10px] uppercase tracking-wider text-amber-700/80 font-semibold">Delvis</div>
                </div>
                <div className="rounded-lg bg-red-50 border border-red-100 p-2">
                  <div className="text-2xl font-bold text-red-700" data-testid="consume-missing-count">{consumeResult.missing_count || 0}</div>
                  <div className="text-[10px] uppercase tracking-wider text-red-700/80 font-semibold">Manglet</div>
                </div>
              </div>

              {(consumeResult.used || []).length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wider font-semibold text-stone-500 mb-1">Trukket fra</div>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {consumeResult.used.map((u, idx) => (
                      <div key={`used-${idx}-${u.pantry_item_id || u.name}`} className="flex items-center justify-between gap-2 text-xs bg-emerald-50/60 rounded px-2 py-1">
                        <span className="text-stone-700 truncate">{u.name}</span>
                        <span className="text-emerald-700 font-medium flex-shrink-0">−{u.amount} {u.unit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(consumeResult.missing || []).length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wider font-semibold text-stone-500 mb-1">Manglet i beholdning</div>
                  <div className="max-h-28 overflow-y-auto space-y-1">
                    {consumeResult.missing.map((m, idx) => (
                      <div key={`missing-${idx}-${m.ingredient}`} className="flex items-center justify-between gap-2 text-xs bg-red-50/60 rounded px-2 py-1">
                        <span className="text-stone-700 truncate">{m.ingredient}</span>
                        <span className="text-red-700 font-medium flex-shrink-0">{m.amount}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-2 justify-end pt-1">
                {(consumeResult.missing || []).length > 0 && (
                  <button
                    onClick={async () => {
                      // Add missing ingredients to shopping list
                      try {
                        const weekStart = (await import("@/lib/api")).getWeekStart();
                        const slRes = await api.getShoppingList(weekStart, "", activeShift);
                        const sl = slRes.data;
                        const newItems = consumeResult.missing.map((m) => {
                          const parts = (m.amount || "").trim().split(" ");
                          return {
                            id: crypto.randomUUID(),
                            name: m.ingredient,
                            amount: parts[0] || "",
                            unit: parts.slice(1).join(" ") || "stk",
                            category: "Other",
                            checked: false,
                            status: "",
                            is_manual: true,
                          };
                        });
                        await api.updateShoppingList(weekStart, { ...sl, items: [...(sl.items || []), ...newItems] });
                        toast.success(`${newItems.length} manglende varer lagt i handlelista`);
                      } catch {
                        toast.error("Kunne ikke legge manglende varer i handlelista");
                      }
                    }}
                    className="rounded-lg border border-navy-200 bg-white px-3 py-2 text-xs font-medium text-navy-800 hover:bg-navy-50"
                    data-testid="consume-add-missing-to-list-btn"
                  >
                    Legg manglende i handleliste
                  </button>
                )}
                <button
                  onClick={() => { setConsumeOpen(false); setConsumeResult(null); }}
                  className="rounded-lg bg-stone-800 px-4 py-2 text-sm font-medium text-white hover:bg-stone-900"
                  data-testid="consume-close-btn"
                >
                  Lukk
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Change image dialog */}
      <Dialog open={imageDialogOpen} onOpenChange={setImageDialogOpen}>
        <DialogContent className="max-w-sm" data-testid="change-image-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Cormorant Garamond', serif" }}>Bytt bilde</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-stone-500">Velg hvordan du vil oppdatere bildet av retten.</p>

            <button
              onClick={handleRegenerateImage}
              disabled={imageLoading}
              className="w-full flex items-center gap-3 rounded-lg border border-navy-100 bg-sailcloth-100 hover:bg-navy-50 px-4 py-3 text-left transition-colors disabled:opacity-50"
              data-testid="regenerate-image-btn"
            >
              <div className="h-10 w-10 rounded-full bg-seawater-600 text-white flex items-center justify-center flex-shrink-0">
                <Sparkles className="h-5 w-5" />
              </div>
              <div>
                <div className="text-sm font-semibold text-navy-900">Generer med AI</div>
                <div className="text-xs text-navy-800/80">Nano Banana lager et nytt matbilde (15-30 sek)</div>
              </div>
            </button>

            <label
              className={`flex items-center gap-3 rounded-lg border border-stone-200 bg-white hover:bg-stone-50 px-4 py-3 cursor-pointer transition-colors ${imageLoading ? "opacity-50 pointer-events-none" : ""}`}
              data-testid="upload-image-btn"
            >
              <div className="h-10 w-10 rounded-full bg-stone-700 text-white flex items-center justify-center flex-shrink-0">
                <Upload className="h-5 w-5" />
              </div>
              <div>
                <div className="text-sm font-semibold text-stone-800">Last opp eget bilde</div>
                <div className="text-xs text-stone-500">JPEG/PNG – maks 5 MB</div>
              </div>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleUploadImage(e.target.files?.[0])}
                data-testid="upload-image-input"
              />
            </label>
          </div>
        </DialogContent>
      </Dialog>

      <AddToPlanDialog open={addToPlanOpen} onOpenChange={setAddToPlanOpen} recipe={recipe} />
    </div>
  );
}
